import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Radio, notification } from 'antd';
import { ArrowsClockwise, CheckCircle, Warning, X } from '@phosphor-icons/react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import opportunityInfoServices from '../../../../services/oneButtonServices/opportunityInfoService';
import { TitleContact, AddTitleContactsModalProps } from '../../../../@types/oneButtonType';
import DashboardModal from './DashboardModal';
import { OBDSelect } from './form-elements';
import './oneButton-css/AddTitleContactsModal.css';

// --- Sub-components ---
const ContactTableRow: React.FC<{
    row: TitleContact;
    index: number;
    theme: string;
    existingContacts: { label: string; value: string }[];
    onRowSelect: (index: number) => void;
    onModeChange: (index: number, mode: 'create' | 'update') => void;
    onContactSelect: (index: number, contactId: string) => void;
}> = React.memo(({ row, index, theme, existingContacts, onRowSelect, onModeChange, onContactSelect }) => (
    <tr className={`obd-atc-modal__tr ${row.isSelected ? 'is-selected' : ''}`}>
        <td className="obd-atc-modal__td">
            <input 
                type="checkbox" 
                className="obd-atc-modal__checkbox"
                checked={row.isSelected} 
                onChange={() => onRowSelect(index)} 
            />
        </td>
        <td className="obd-atc-modal__td">{row.firstName}</td>
        <td className="obd-atc-modal__td">{row.lastName}</td>
        <td className="obd-atc-modal__td">{row.street}</td>
        <td className="obd-atc-modal__td">{row.city}</td>
        <td className="obd-atc-modal__td">{row.state}</td>
        <td className="obd-atc-modal__td">{row.zipCode}</td>
        <td className="obd-atc-modal__td" style={{ textAlign: 'center' }}>
            {row.isOwner && <CheckCircle size={20} color="#10b981" weight="fill" />}
        </td>
        <td className="obd-atc-modal__td">{row.useCode}</td>
        <td className="obd-atc-modal__td">{row.useCodeDescription}</td>
        <td className="obd-atc-modal__td">{row.ownerName}</td>
        <td className="obd-atc-modal__td">
            <Radio.Group 
                onChange={(e) => onModeChange(index, e.target.value)} 
                value={row.IsUpdate ? 'update' : 'create'}
                className="obd-atc-modal__radio-group"
                disabled={!row.isSelected}
            >
                <Radio value="create">Create</Radio>
                <Radio value="update">Update</Radio>
            </Radio.Group>
        </td>
        <td className="obd-atc-modal__td">
            {row.IsShowContactsOption && row.isSelected && (
                <OBDSelect
                    placeholder="Select contact"
                    className="obd-atc-modal__select-contact"
                    options={existingContacts}
                    style={{ width: '100%', minWidth: '180px' }}
                    onChange={(v) => onContactSelect(index, v)}
                    value={row.OpportunityContactId || undefined}
                    popupClassName={`obd-select-dropdown obd-theme-${theme}`}
                />
            )}
        </td>
    </tr>
));

ContactTableRow.displayName = 'ContactTableRow';

// --- Main Component ---
export const AddTitleContactsModal: React.FC<AddTitleContactsModalProps> = ({ open, setOpen, initialData, onSuccess }) => {
    const theme = useOneButtonStore((s) => s.theme);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const opportunityId = useOneButtonStore((s) => s.opportunityID);

    const [dataSource, setDataSource] = useState<TitleContact[]>([]);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const [noticeAPI, contextHolder] = notification.useNotification();

    const showNotice = useCallback((type: 'success' | 'error' | 'warning' | 'info', message: string, description?: string) => {
        noticeAPI[type]({
            message,
            description,
            placement: 'topRight',
            className: `obd-notification obd-theme-${theme}`,
            style: { zIndex: 10000 }
        });
    }, [noticeAPI, theme]);

    const existingContacts = useMemo(() => {
        return (opportunityDetails?.Customers || []).map((c: any) => ({
            label: `${c.FirstName} ${c.LastName}`,
            value: c.CustomerId || c.customerId
        }));
    }, [opportunityDetails]);

    const fetchData = useCallback(async () => {
        if (!opportunityId || !opportunityDetails) return;

        const mapOwners = (rawData: any) => (Array.isArray(rawData) ? rawData : [rawData]).map((item: any, idx: number) => ({
            firstName: item.FirstName || item.firstName || '',
            lastName: item.LastName || item.lastName || '',
            street: item.Address || item.street || item.Street || '',
            city: item.City || item.city || '',
            state: item.State || item.state || '',
            zipCode: item.ZIP || item.zipCode || item.ZipCode || '',
            isOwner: item.isOwner !== undefined ? item.isOwner : !!item.Owner,
            useCode: item.UseCode || item.useCode || '',
            useCodeDescription: item.UseCodeDescription || item.useCodeDescription || '',
            ownerName: item.Owner || item.ownerName || item.owner || '',
            isSelected: idx === 0, 
            IsShowContactsOption: false,
            IsUpdate: false,
            OpportunityContactId: null
        }));

        if (Array.isArray(initialData) && initialData.length > 0) {
            setDataSource(mapOwners(initialData));
            return;
        }

        setLoading(true);
        setErrorMsg(null);
        try {
            const addr = opportunityDetails.InstallationAddress;
            const res = await opportunityInfoServices.getCustomerTitleInfooneLead(
                opportunityDetails.DirectLeadID, 
                addr?.Street || '', 
                (addr?.ZipCode || addr?.zipCode || '').slice(0, 5), 
                addr?.Latitude || '', 
                addr?.Longitude || ''
            );
            const rawData = Array.isArray(res) ? res : (res?.data?.locations?.[0]?.owners || res?.data || []);
            setDataSource(mapOwners(rawData));
        } catch (err) {
            console.error("AddTitleContactsModal - Fetch failed", err);
            setErrorMsg("Failed to fetch title contacts.");
        } finally {
            setLoading(false);
        }
    }, [opportunityId, opportunityDetails, initialData]);

    useEffect(() => {
        if (open) {
            fetchData();
        } else {
            setDataSource([]);
            setErrorMsg(null);
        }
    }, [open, fetchData]);

    const handleRowSelect = useCallback((index: number) => {
        setDataSource(prev => prev.map((item, i) => ({
            ...item,
            isSelected: i === index
        })));
    }, []);

    const handleModeChange = useCallback((index: number, mode: 'create' | 'update') => {
        setDataSource(prev => prev.map((item, i) => {
            if (i !== index) return item;
            return {
                ...item,
                IsUpdate: mode === 'update',
                IsShowContactsOption: mode === 'update',
                OpportunityContactId: mode === 'create' ? null : item.OpportunityContactId
            };
        }));
    }, []);

    const handleContactSelect = useCallback((index: number, contactId: string) => {
        setDataSource(prev => prev.map((item, i) => {
            if (i !== index) return item;
            return {
                ...item,
                OpportunityContactId: contactId
            };
        }));
    }, []);

    const handleSync = async () => {
        const selected = dataSource.find(d => d.isSelected);
        if (!selected) return;

        if (selected.IsUpdate && !selected.OpportunityContactId) {
            return;
        }

        setErrorMsg(null);
        setSyncing(true);
        try {
            const payload = [{
                FirstName: selected.firstName,
                LastName: selected.lastName,
                Address: selected.street,
                City: selected.city,
                State: selected.state,
                ZIP: selected.zipCode,
                titleVerified: true,
                UseCode: selected.useCode,
                UseCodeDescription: selected.useCodeDescription,
                Owner: selected.ownerName,
                IsUpdate: selected.IsUpdate,
                OpportunityContactId: selected.OpportunityContactId,
                IsShowContactsOption: selected.IsShowContactsOption,
                isSelected: selected.isSelected
            }];

            const response = await opportunityInfoServices.opportunityContactTitleUpdate({ 
                opportunityId: opportunityId!, 
                contacts: payload 
            });
            
            if (response === true || response === "true") {
                showNotice('success', 'Success', 'Contact Updated successfully');
                onSuccess?.();
            } else {
                showNotice('error', 'Sync Failed', 'The update was not successful. Please check the details and try again.');
            }
        } catch (err) {
            console.error("AddTitleContactsModal - Sync failed", err);
            showNotice('error', 'Error', 'An unexpected error occurred during the update.');
        } finally {
            setSyncing(false);
        }
    };

    const isBusy = loading || syncing;
    const haveError = dataSource.some(row => row.isSelected && row.IsShowContactsOption && !row.OpportunityContactId);

    return (
        <DashboardModal
            open={open}
            onClose={() => setOpen(false)}
            title="Add title contacts"
            footer={
                <div className="obd-atc-modal__actions">
                    {haveError && (
                        <div className="obd-atc-modal__message obd-atc-modal__message--warning">
                            <Warning size={18} weight="fill" />
                            <span>You have chosen to Update Contact, please choose a contact from the drop down list</span>
                        </div>
                    )}
                    {errorMsg && !haveError && (
                        <div className="obd-atc-modal__message obd-atc-modal__message--error">
                            {errorMsg}
                        </div>
                    )}
                    <div className="obd-atc-modal__actions-right">
                        <button 
                            type="button" 
                            className="obd-atc-modal__btn obd-atc-modal__btn--cancel" 
                            onClick={() => setOpen(false)}
                            disabled={syncing}
                        >
                            <X size={14} weight="bold" />
                            Close
                        </button>
                        <button 
                            type="button" 
                            className="obd-atc-modal__btn obd-atc-modal__btn--primary"
                            onClick={handleSync}
                            disabled={isBusy || dataSource.length === 0 || !dataSource.some(d => d.isSelected)}
                        >
                            <ArrowsClockwise size={14} weight="bold" />
                            Sync Contacts on Title
                        </button>
                    </div>
                </div>
            }
            width="95vw"
            theme={theme}
            className={`obd-add-title-contacts-modal obd-theme-${theme}`}
            rootClassName={`obd-add-title-contacts-modal-root obd-theme-${theme}`}
            maskClosable={false}
            destroyOnClose
            loading={isBusy}
            loadingMessage={loading ? 'Fetching title contacts...' : 'Syncing contacts...'}
        >
            <div className="obd-atc-modal__inner" style={{ position: 'relative' }}>
                {contextHolder}

                <div className="obd-atc-modal__body">
                    <div className="obd-atc-modal__table-container">
                        <table className="obd-atc-modal__table">
                            <thead>
                                <tr>
                                    <th className="obd-atc-modal__th"></th>
                                    <th className="obd-atc-modal__th">First Name</th>
                                    <th className="obd-atc-modal__th">Last Name</th>
                                    <th className="obd-atc-modal__th">Address</th>
                                    <th className="obd-atc-modal__th">City</th>
                                    <th className="obd-atc-modal__th">State</th>
                                    <th className="obd-atc-modal__th">Zip</th>
                                    <th className="obd-atc-modal__th">Title Verified</th>
                                    <th className="obd-atc-modal__th">Use Code</th>
                                    <th className="obd-atc-modal__th">Use Code Description</th>
                                    <th className="obd-atc-modal__th">Owner</th>
                                    <th className="obd-atc-modal__th">Create/Update</th>
                                    <th className="obd-atc-modal__th">Select Contact</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dataSource.length === 0 ? (
                                    <tr>
                                        <td colSpan={13} style={{ textAlign: 'center', padding: '32px' }} className="obd-atc-modal__td">
                                            <div style={{ opacity: 0.6 }}>No title contacts found.</div>
                                        </td>
                                    </tr>
                                ) : (
                                    dataSource.map((row, idx) => (
                                        <ContactTableRow 
                                            key={idx}
                                            row={row}
                                            index={idx}
                                            theme={theme}
                                            existingContacts={existingContacts}
                                            onRowSelect={handleRowSelect}
                                            onModeChange={handleModeChange}
                                            onContactSelect={handleContactSelect}
                                        />
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>
        </DashboardModal>
    );
};

export default AddTitleContactsModal;
